import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { defaultTo } from 'lodash-es';
import { Observable } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { List, Project, User } from '@site-mate/dashpivot-shared-library';

import { AbstractListDetailsPageComponent } from 'app/lists/abstract-list-details-page/abstract-list-details-page.component';
import { ListItemsService } from 'app/lists/list-items.service';
import { ListsService } from 'app/lists/lists.service';
import { SegmentService } from 'app/segment/segment.service';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { ListsHandler } from 'app/shared/model/lists/lists-handler.model';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { EventsService } from 'app/shared/service/events/events.service';
import { ProjectService } from 'app/shared/service/project.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-list-project-details-page',
  templateUrl: '../abstract-list-details-page/abstract-list-details-page.component.html',
  styleUrls: ['../abstract-list-details-page/abstract-list-details-page.component.scss'],
})
export class ListProjectDetailsPageComponent extends AbstractListDetailsPageComponent implements OnInit {
  constructor(
    activatedRoute: ActivatedRoute,
    confirmService: ConfirmService,
    errorHandler: ErrorHandler,
    protected readonly listItemsService: ListItemsService,
    protected readonly listsService: ListsService,
    protected readonly projectService: ProjectService,
    router: Router,
    segmentService: SegmentService,
    toastrService: ToastrService,
    userService: UserService,
    eventsService: EventsService,
  ) {
    super(
      activatedRoute,
      confirmService,
      errorHandler,
      listItemsService,
      listsService,
      router,
      segmentService,
      toastrService,
      userService,
      eventsService,
    );
  }

  get canEditData() {
    return this.userService.isProjectControllerOrAbove();
  }

  ngOnInit() {
    this.setupInitialData();
  }

  setupInitialData() {
    this.activatedRoute.params
      .pipe(
        takeUntil(this.onUnsubscribe),
        filter((params) => params.projectId),
        switchMap((params) => this.getProjectAndUpdateState(params)),
        switchMap((project) => this.updateProjectAndFetchSubscribableUsers(project)),
        tap((subscribableUsers) => this.subscribableUsers$.next(subscribableUsers)),
        switchMap(() => this.activatedRoute.params),
        filter((params) => params.listId),
        switchMap(() => this.updateListById()),
        catchError(this.handleErrorAndContinue.bind(this)),
      )
      .subscribe();
  }

  private getProjectAndUpdateState(params): Observable<Project> {
    this.parentId = params.projectId;
    this.state.selectedListId = params.listId;
    return this.projectService.getProject(this.parentId);
  }

  private updateProjectAndFetchSubscribableUsers(project: Project): Observable<User[]> {
    this.folder = { hierarchy: AppHierarchy.Project, project };
    return this.getSubscribableUsers();
  }

  private updateListById() {
    return this.getList(this.state.selectedListId).pipe(
      tap(() => this.listsHandler.setSelectedListById(this.state.selectedListId, this.timezone)),
    );
  }

  getSubscribableUsers(): Observable<User[]> {
    return this.projectService.getUsers(this.parentId).pipe(
      map((users) =>
        users.filter((user) => {
          const projectControllerOf = defaultTo(user.projectControllerOf, []);
          const companyControllerOf = defaultTo(user.companyControllerOf, []);
          return projectControllerOf.includes(this.parentId) || companyControllerOf.includes(this.parentId);
        }),
      ),
    );
  }

  getParentLists(projectId: string) {
    return this.listsService.getProjectMetaDataList(projectId);
  }

  deleteList(projectId: string, listId: string) {
    return this.listsService.removeProjectList(projectId, listId);
  }

  updateList(projectId: string, listId: string, list: List) {
    return this.listsService.updateProjectList(projectId, listId, list);
  }

  uploadList(projectId: string, listId: string, data: FormData) {
    return this.listsService.uploadUpdateProjectList(projectId, listId, data);
  }

  async onListDeployedViaCsv(list: List, fileList: FileList) {
    const csvFile = fileList[0];
    const form = new FormData();
    form.append('file', csvFile);
    try {
      this.state.isUploading = true;
      const updatedList = await this.listItemsService
        .deployListViaCsv(list.id, this.parentId, form)
        .toPromise();
      const filteredList = ListsHandler.service.filterDeployedListItems(updatedList, this.parentId);
      this.listsHandler.updateList(filteredList.id, filteredList, this.timezone);
    } catch (error) {
      this.errorHandler.handle(error);
    }
    this.state.isUploading = false;
  }
}
